import { Node as CraftNode } from '@candulabs/craft';
import { css, styled } from 'canoe';
import React, { ReactElement } from 'react';

import { FormInputValidation } from '@candu-ai/forms/document-types';
import { useManageFormFieldValidation } from '@candu-ai/forms/editor-utils';
import Node from 'craft/components/Node/Node';
import { BuiltInComponents } from 'craft/constants';
import { useEditorNode, useNodeContext } from 'craft/hooks';
import { useFormAncestor, useFormIdFromAncestor } from 'craft/state';
import { ToolboxComponentSections } from 'craft/types';

import { FormFieldValidationMessage } from './FormFieldValidationMessage';
import { FormLabel } from './FormLabel';
import { getFormFieldNodeRules } from './helpers';
import { SelectCardGroupToolbox } from './toolbox/SelectCardGroupToolbox';

const SelectCardWrapper = styled.div<{ columns: number }>`
  ${({ columns, theme }) => css`
    display: grid;
    grid-template-columns: repeat(${columns}, 1fr);
    grid-gap: ${theme.spaces.xs};
  `}
`;

interface FormMultiSelectCardGroupProps {
  children?: ReactElement | ReactElement[];
  formInputId: string;
  formId?: string;
  label: string;
  columns?: number;
  showLabel?: boolean;
  validation?: FormInputValidation[];
}

export const FormMultiSelectCardGroup = ({
  children,
  formId,
  label,
  columns = 6,
  showLabel,
  validation,
}: FormMultiSelectCardGroupProps) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const { setProps } = useEditorNode(id);

  useFormIdFromAncestor(formId);

  const {
    validationIndexMapping,
    isRequired,
    onSetAtomicValidationProps,
  } = useManageFormFieldValidation({
    setProps,
    validation,
  });

  return (
    <Node data-type="form-multiselect-card-group">
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      {showLabel && <FormLabel id={id} required={isRequired} label={label} />}

      <SelectCardWrapper columns={columns}>{children}</SelectCardWrapper>
      {isRequired && shouldDisplayErrorState && (
        <FormFieldValidationMessage
          nodeId={id}
          validationKey="required"
          onSetProps={(value) => onSetAtomicValidationProps(value, 'required')}
          message={validation?.[validationIndexMapping.required]?.message}
        />
      )}
    </Node>
  );
};

FormMultiSelectCardGroup.craft = {
  name: BuiltInComponents.FormMultiSelectCardGroup,
  displayName: 'Form: Multi-select Cards',
  isCanvas: true,
  rules: {
    ...getFormFieldNodeRules(),
    canMoveIn: (incomingNodes: CraftNode[]) =>
      incomingNodes.every((node) => node.data.name === BuiltInComponents.FormMultiSelectCard),
  },
};

FormMultiSelectCardGroup.toolbox = {
  component: [SelectCardGroupToolbox],
  schema: {
    [ToolboxComponentSections.Border]: {
      isVisible: false,
    },
    [ToolboxComponentSections.Shadow]: {
      isVisible: false,
    },
  },
};

FormMultiSelectCardGroup.selectedToolbar = {};
